import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, LoginForm, LoginResponse } from '../../types';
import { authService } from '../../services/authService';

// 异步登录action
export const loginUser = createAsyncThunk(
  'auth/login',
  async (loginData: LoginForm): Promise<LoginResponse> => {
    const response = await authService.login(loginData);
    return response;
  }
);

// 异步退出登录action
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (): Promise<void> => {
    await authService.logout();
  }
);

// 获取用户信息action
export const getUserInfo = createAsyncThunk(
  'auth/getUserInfo',
  async (): Promise<User> => {
    const user = await authService.getUserInfo();
    return user;
  }
);

// 状态接口
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

// 从 localStorage 恢复初始状态
const getInitialState = (): AuthState => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');

  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      return {
        isAuthenticated: true,
        user,
        token,
        loading: false,
        error: null,
      };
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
      // 清除无效数据
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }

  return {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: false,
    error: null,
  };
};

// 初始状态
const initialState: AuthState = getInitialState();

// 创建slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 清除错误
    clearError: (state) => {
      state.error = null;
    },
    // 更新用户信息
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    // 从localStorage恢复状态
    restoreAuthState: (state) => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          state.isAuthenticated = true;
          state.token = token;
          state.user = user;
        } catch (error) {
          console.error('Failed to parse user from localStorage:', error);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // 登录
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;

        // 持久化到localStorage
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '登录失败';
      })

      // 登出
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;

        // 清除localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '登出失败';
      })

      // 获取用户信息
      .addCase(getUserInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取用户信息失败';
      });
  },
});

export const { clearError, updateUser, restoreAuthState } = authSlice.actions;
export default authSlice.reducer;